import { useMemo } from 'react';
import { useGetAccountIntegrationsQuery } from '@/store/query/job-boards';
import { useKFlags } from '@/hooks/use-k-flags';
import { ESIGNATURE_PROVIDERS } from '@/constants/esignature';

/**
 * Is DocuSign connected for this account, and is the stage-activity feature on?
 *
 * Unlike every other integration gate, this one cannot come from `use-integrations`:
 * bootstrap exposes `connected_hackerrank`, `connected_checkr` and friends on the
 * account, but nothing equivalent for e-signature providers. The authoritative
 * source is `GET /account_integrations?scope=e_signatures`, which is what the
 * settings screen reads. RTK Query caches it under `GetJobBoardsIntegrations`, so
 * the several callers here (activity picker, stage headers) share one request.
 *
 * If the backend later adds a bootstrap flag, this collapses into
 * `useIntegrations` as `isDocusignEnabled` and the fetch goes away.
 */
export const useDocusignConnection = () => {
  const { tDocusignOnAllStages } = useKFlags();

  const { data, isFetching } = useGetAccountIntegrationsQuery(
    { scope: 'e_signatures' },
    {
      // Nothing downstream renders while the flag is off, so don't spend a
      // request on it.
      skip: !tDocusignOnAllStages,
      // The ATS base sets `refetchOnMountOrArgChange: true` and this endpoint
      // sets `staleTime: 0`, so every consumer that mounts fires the request
      // again. The activity picker mounts fresh on each open, which made the
      // Docusign card pop in a beat late every single time. An account's
      // integration list does not change mid-session, so the cache is good
      // enough and only the first mount pays.
      refetchOnMountOrArgChange: false,
    }
  );

  const isDocusignConnected = useMemo(() => {
    const integrations = data?.account_integrations ?? [];
    return integrations.some(
      (integration: { integration_type?: string; id?: string | number }) =>
        integration?.integration_type === ESIGNATURE_PROVIDERS.DOCUSIGN &&
        Boolean(integration?.id)
    );
  }, [data?.account_integrations]);

  return {
    /** Flag on AND an active DocuSign integration — the gate every surface uses. */
    isEsignActivityEnabled: Boolean(
      tDocusignOnAllStages && isDocusignConnected
    ),
    isDocusignConnected,
    isEsignActivityFlagOn: Boolean(tDocusignOnAllStages),
    isLoading: isFetching,
  };
};

/**
 * Warms {@link useDocusignConnection}'s cache.
 *
 * The activity picker only mounts when the drawer opens, so without this the
 * request starts at the moment the recruiter is already looking at the list.
 * Calling it from the interview plan means the answer is usually in hand before
 * the drawer is ever opened. Deliberately returns nothing — this is a prefetch,
 * not a gate.
 */
export const usePrefetchDocusignConnection = (): void => {
  useDocusignConnection();
};
